/**
 * LiveTrackDialog — shows the live driver position for a ride that is currently
 * in_progress (or accepted = driver is on the way to pickup). Polls
 * /api/(customer|business|rides)/{id}/track every 10s and renders TripMap.
 *
 * Used from the rider My Trips and business Trips list. The wrapping caller
 * picks the correct API client (customerApi / businessApi / api).
 */
import { useState, useEffect, useMemo, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import TripMap from "@/components/TripMap";
import useFleetStream from "@/hooks/useFleetStream";
import { Radio, Phone, Car, Loader2 } from "lucide-react";

export default function LiveTrackDialog({ rideId, apiClient, scope = "customer", buttonClassName = "", buttonLabel = "Track Driver Live" }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  // v2.6.9 batch BI · open a real-time WS stream while the dialog is open.
  // The /api/ws/rider endpoint only authorizes streaming when the trip is in
  // an active state — if the server rejects us with 1008, the hook simply
  // stays closed and we fall back to the 10 s HTTP poll below (which still
  // works for non-active scopes like the business / driver previews).
  const customerToken = scope === "customer" && typeof window !== "undefined"
    ? localStorage.getItem("rtg_customer_token") : null;
  const { events: wsEvents, status: wsStatus } = useFleetStream({
    role: "rider", token: customerToken, active: open && !!customerToken,
  });

  const path = scope === "business"
    ? `/business/rides/${rideId}/track`
    : scope === "driver"
      ? `/rides/${rideId}/track`
      : `/customer/rides/${rideId}/track`;

  const poll = async () => {
    try {
      const { data: d } = await apiClient.get(path);
      setData(d); setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not fetch live position");
    }
  };

  useEffect(() => {
    if (!open) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return;
    }
    poll();
    timerRef.current = setInterval(poll, 10000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); timerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rideId]);

  // v2.6.9 batch BI · prefer the WS stream's last driver_ping over the
  // 10-second polled snapshot. We extract the latest ping event of type
  // "driver_ping" from useFleetStream's rolling buffer.
  const wsDriverPing = useMemo(() => {
    if (!wsEvents || !wsEvents.length) return null;
    for (let i = wsEvents.length - 1; i >= 0; i -= 1) {
      const e = wsEvents[i];
      if (e?.type === "driver_ping" && e.lat != null && e.lng != null) return e;
    }
    return null;
  }, [wsEvents]);

  const livePosition = wsDriverPing
    ? { lat: wsDriverPing.lat, lng: wsDriverPing.lng }
    : (data?.driver_loc ? { lat: data.driver_loc.lat, lng: data.driver_loc.lng } : null);
  const livePingTs = wsDriverPing?.ts || data?.driver_loc?.ts;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`track-live-btn-${rideId}`} size="sm" variant="outline" className={`bg-zinc-950 border-amber-400/40 text-amber-400 ${buttonClassName}`}>
          <Radio className="h-3 w-3 mr-1"/>{buttonLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-900 border-amber-400/40 text-zinc-100 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <Radio className="h-5 w-5"/>Live Tracking · {data?.waybill_no || rideId.slice(0,8)}
            {data?.status && <span className="text-[10px] uppercase tracking-widest border border-amber-400/40 rounded-full px-2 py-0.5 ml-1">{data.status.replace("_", " ")}</span>}
          </DialogTitle>
        </DialogHeader>

        {error && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/40 rounded p-2" data-testid="track-error">{error}</div>}

        {!data ? (
          <div className="flex items-center justify-center py-12 text-amber-400"><Loader2 className="h-6 w-6 animate-spin"/></div>
        ) : (
          <div className="space-y-3">
            {data.driver && (
              <DriverCard driver={data.driver}/>
            )}
            <TripMap origin={data.origin} destination={data.destination} livePosition={livePosition} height={320} testid="live-track-map"/>
            <div className="text-[11px] text-zinc-500 flex items-center justify-between" data-testid="live-track-status">
              <span>
                {wsStatus === "open"
                  ? <span className="text-green-400">● LIVE · 3-sec stream</span>
                  : (wsStatus === "connecting" ? "Connecting live stream…" : "Polling every 10 seconds")}
              </span>
              {livePingTs && <span>Last ping: {new Date(livePingTs).toLocaleTimeString()}</span>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * v2.6.9 batch AA · Driver card sub-component. Memoizes the vehicle-string
 * concatenation + phone-digit cleaning so they don't re-run on every poll tick.
 */
function DriverCard({ driver }) {
  const vehicleStr = useMemo(
    () => [driver.vehicle_color, driver.vehicle_make, driver.vehicle_model].filter(Boolean).join(" "),
    [driver.vehicle_color, driver.vehicle_make, driver.vehicle_model],
  );
  const phoneHref = useMemo(
    () => (driver.phone ? `tel:${driver.phone.replace(/[^0-9+]/g, "")}` : null),
    [driver.phone],
  );
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded p-3 flex items-center gap-3" data-testid="track-driver-card">
      {driver.profile_picture_url ? (
        <img src={driver.profile_picture_url} alt="" className="h-12 w-12 rounded-full object-cover border border-amber-400/30"/>
      ) : (
        <div className="h-12 w-12 rounded-full bg-amber-400/10 border border-amber-400/30 flex items-center justify-center text-amber-400"><Car className="h-5 w-5"/></div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold truncate">{driver.name}</div>
        <div className="text-[11px] text-zinc-500 truncate">
          {vehicleStr}
          {driver.plate_number ? ` · ${driver.plate_number}` : ""}
        </div>
        <div className="text-[11px] text-zinc-500">{driver.email}</div>
      </div>
      {phoneHref && (
        <a href={phoneHref} className="text-amber-400 hover:text-amber-300" data-testid="track-call-driver">
          <Phone className="h-5 w-5"/>
        </a>
      )}
    </div>
  );
}
